import { z } from 'zod';
import {
  TenantPlanSchema,
  TenantSlugSchema,
  TenantStatusSchema,
  TenantVerticalSchema,
} from './tenants';
import { EmailSchema } from './auth';

/**
 * Platform-admin API + UI contracts (ARCHITECTURE_PLAN §6.4, §9
 * `/api/admin/*`, §10 `/admin`). Single source of truth shared by the Worker
 * (validators) and the SPA (typed client) per §3.
 *
 * The admin surface is NOT tenant-scoped: a platform admin has NO implicit
 * tenant access (§6.4). Impersonation is the ONLY mechanism by which a
 * platform admin touches tenant data, and it scopes them into exactly one
 * tenant with admin-role capabilities MINUS billing.
 */

/** Tenant summary as seen by the platform admin (richer than the SPA's). */
export const AdminTenantSummarySchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  vertical: TenantVerticalSchema,
  status: TenantStatusSchema,
  plan: TenantPlanSchema,
  timezone: z.string(),
  trialEndsAt: z.number().nullable(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type AdminTenantSummary = z.infer<typeof AdminTenantSummarySchema>;

/** `GET /api/admin/tenants` response (keyset-paginated). */
export const AdminTenantsListResponseSchema = z.object({
  tenants: z.array(AdminTenantSummarySchema),
  nextCursor: z.string().nullable(),
});
export type AdminTenantsListResponse = z.infer<typeof AdminTenantsListResponseSchema>;

/** `GET /api/admin/tenants/:id` response. */
export const AdminTenantDetailResponseSchema = z.object({
  tenant: AdminTenantSummarySchema,
});
export type AdminTenantDetailResponse = z.infer<typeof AdminTenantDetailResponseSchema>;

/** One audit-log row as exposed to the platform admin. */
export const AdminAuditEntrySchema = z.object({
  id: z.string(),
  tenantId: z.string().nullable(),
  actorType: z.enum(['user', 'platform_admin', 'system', 'impersonation']),
  actorUserId: z.string().nullable(),
  impersonationGrantId: z.string().nullable(),
  action: z.string(),
  targetType: z.string(),
  targetId: z.string(),
  metadataJson: z.string(),
  createdAt: z.number(),
});
export type AdminAuditEntry = z.infer<typeof AdminAuditEntrySchema>;

/** `GET /api/admin/tenants/:id/audit` response (keyset-paginated). */
export const AdminAuditListResponseSchema = z.object({
  entries: z.array(AdminAuditEntrySchema),
  nextCursor: z.string().nullable(),
});
export type AdminAuditListResponse = z.infer<typeof AdminAuditListResponseSchema>;

/** One impersonation grant as exposed to the platform admin. */
export const AdminImpersonationGrantSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  adminUserId: z.string(),
  reason: z.string(),
  expiresAt: z.number(),
  revokedAt: z.number().nullable(),
  createdAt: z.number(),
  /** Derived: active = not revoked and not expired. */
  active: z.boolean(),
});
export type AdminImpersonationGrant = z.infer<typeof AdminImpersonationGrantSchema>;

/** `GET /api/admin/impersonations` response (keyset-paginated). */
export const AdminImpersonationListResponseSchema = z.object({
  grants: z.array(AdminImpersonationGrantSchema),
  nextCursor: z.string().nullable(),
});
export type AdminImpersonationListResponse = z.infer<typeof AdminImpersonationListResponseSchema>;

/**
 * `POST /api/admin/impersonation` body (§6.4). `tenantId` selects the tenant
 * the admin is impersonating INTO; `reason` is required non-empty (enforced
 * again server-side). The TTL is fixed at the §6.4 max (60 min) and not
 * client-selectable — a platform admin may not extend it.
 */
export const StartImpersonationBodySchema = z.object({
  tenantId: z.string().min(1),
  reason: z.string().trim().min(1, 'A reason is required to start an impersonation.').max(500),
});
export type StartImpersonationBody = z.infer<typeof StartImpersonationBodySchema>;

/** `POST /api/admin/impersonation` response — the grant + session cookie set. */
export const StartImpersonationResponseSchema = z.object({
  grant: AdminImpersonationGrantSchema,
  /** The tenant slug the impersonated session resolves into (for SPA redirect). */
  tenantSlug: z.string(),
});
export type StartImpersonationResponse = z.infer<typeof StartImpersonationResponseSchema>;

/** `POST /api/admin/impersonation/:grantId/revoke` response. */
export const RevokeImpersonationResponseSchema = z.object({
  grant: AdminImpersonationGrantSchema,
});
export type RevokeImpersonationResponse = z.infer<typeof RevokeImpersonationResponseSchema>;

/**
 * Admin tenant provisioning (`POST /api/admin/tenants`) — the super-user "new
 * tenant setup" flow: owner user + company + account (plan/status/trial) +
 * optional business hours + optional Stripe customer, in one call.
 */

/** IANA timezone check, duplicated from tenants.ts (that one is not exported). */
const AdminTimezoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .refine(
    (tz) => {
      try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
      } catch {
        return false;
      }
    },
    { message: 'timezone must be a valid IANA name (e.g. America/New_York)' }
  );

/** One weekly business-hours rule (mirrors `availability_rules`, migration 0003). */
export const AdminBusinessHourSchema = z
  .object({
    /** 0 = Sunday … 6 = Saturday (matches the `availability_rules.weekday` CHECK). */
    weekday: z.number().int().min(0).max(6),
    startMinute: z
      .number()
      .int()
      .min(0)
      .max(24 * 60 - 1),
    endMinute: z
      .number()
      .int()
      .min(1)
      .max(24 * 60),
    slotMinutes: z.number().int().min(5).max(240).default(60),
  })
  .refine((rule) => rule.endMinute > rule.startMinute, {
    message: 'business hours must end after they start',
  });
export type AdminBusinessHour = z.infer<typeof AdminBusinessHourSchema>;

/** `POST /api/admin/tenants` body. */
export const AdminCreateTenantBodySchema = z.object({
  owner: z.object({
    email: EmailSchema,
    name: z.string().trim().min(1).max(120),
  }),
  company: z.object({
    name: z.string().trim().min(1).max(120),
    slug: TenantSlugSchema,
    vertical: TenantVerticalSchema,
    timezone: AdminTimezoneSchema,
  }),
  account: z.object({
    plan: TenantPlanSchema,
    status: TenantStatusSchema,
    /** Epoch ms; only meaningful with plan/status `trial`. */
    trialEndsAt: z.number().int().positive().nullable(),
    /** Create + attach a Stripe Customer now (requires a live Stripe key). */
    createStripeCustomer: z.boolean(),
  }),
  businessHours: z.array(AdminBusinessHourSchema).max(21),
});
export type AdminCreateTenantBody = z.infer<typeof AdminCreateTenantBodySchema>;

/** `POST /api/admin/tenants` response. */
export const AdminCreateTenantResponseSchema = z.object({
  tenant: AdminTenantSummarySchema,
  owner: z.object({
    id: z.string(),
    email: z.string(),
    /** False when the email already had a user row (it was linked, not created). */
    created: z.boolean(),
  }),
  stripeCustomerId: z.string().nullable(),
});
export type AdminCreateTenantResponse = z.infer<typeof AdminCreateTenantResponseSchema>;

/**
 * Per-tenant provider secrets (migration 0006 `tenant_secrets`). The service
 * list is a closed enum; `createUrl` powers the admin UI's "create key" quick
 * links next to each field. Values are write-only: the API returns only a
 * `last4` display hint, never the stored secret.
 */
export const TENANT_SECRET_SERVICES = [
  {
    service: 'twilio_account_sid',
    label: 'Twilio Account SID',
    provider: 'Twilio',
    createUrl: 'https://console.twilio.com',
  },
  {
    service: 'twilio_auth_token',
    label: 'Twilio Auth Token',
    provider: 'Twilio',
    createUrl: 'https://console.twilio.com',
  },
  {
    service: 'vapi_api_key',
    label: 'Vapi API Key',
    provider: 'Vapi',
    createUrl: 'https://dashboard.vapi.ai',
  },
  {
    service: 'vapi_webhook_secret',
    label: 'Vapi Webhook Secret',
    provider: 'Vapi',
    createUrl: 'https://dashboard.vapi.ai',
  },
  {
    service: 'stripe_secret_key',
    label: 'Stripe Secret Key',
    provider: 'Stripe',
    createUrl: 'https://dashboard.stripe.com/apikeys',
  },
  {
    service: 'resend_api_key',
    label: 'Resend API Key',
    provider: 'Resend',
    createUrl: 'https://resend.com/api-keys',
  },
] as const;

export const TenantSecretServiceSchema = z.enum([
  'twilio_account_sid',
  'twilio_auth_token',
  'vapi_api_key',
  'vapi_webhook_secret',
  'stripe_secret_key',
  'resend_api_key',
]);
export type TenantSecretService = z.infer<typeof TenantSecretServiceSchema>;

/** One stored secret as exposed to the admin UI (masked — never the value). */
export const AdminTenantSecretSchema = z.object({
  service: TenantSecretServiceSchema,
  last4: z.string(),
  updatedAt: z.number(),
});
export type AdminTenantSecret = z.infer<typeof AdminTenantSecretSchema>;

/** `GET /api/admin/tenants/:id/secrets` response. */
export const AdminTenantSecretsResponseSchema = z.object({
  secrets: z.array(AdminTenantSecretSchema),
});
export type AdminTenantSecretsResponse = z.infer<typeof AdminTenantSecretsResponseSchema>;

/** `PUT /api/admin/tenants/:id/secrets/:service` body. */
export const AdminPutTenantSecretBodySchema = z.object({
  value: z.string().min(4, 'secret value must be at least 4 characters').max(2048),
});
export type AdminPutTenantSecretBody = z.infer<typeof AdminPutTenantSecretBodySchema>;

/** `PUT /api/admin/tenants/:id/secrets/:service` response. */
export const AdminPutTenantSecretResponseSchema = z.object({
  secret: AdminTenantSecretSchema,
});
export type AdminPutTenantSecretResponse = z.infer<typeof AdminPutTenantSecretResponseSchema>;

/** `DELETE /api/admin/tenants/:id/secrets/:service` response. */
export const AdminDeleteTenantSecretResponseSchema = z.object({
  deleted: z.literal(true),
});
export type AdminDeleteTenantSecretResponse = z.infer<typeof AdminDeleteTenantSecretResponseSchema>;
